/**
 * Container Style Definitions
 * Provides consistent styling for different container types
 */

export interface ContainerStyleConfig {
  bgColor: string
  textColor: string
  borderColor: string
  borderStyle?: 'solid' | 'dashed' | 'dotted'
  icon: string
}

export interface CloudProviderStyles {
  aws: ContainerStyleConfig
  azure: ContainerStyleConfig
  gcp: ContainerStyleConfig
  generic: ContainerStyleConfig
}

export interface ContainerStyles {
  'cloud-provider': CloudProviderStyles
  'layer': ContainerStyleConfig
  'network-zone': ContainerStyleConfig
  'service-group': ContainerStyleConfig
}

/**
 * Professional color palette for containers
 * Each type has distinct, recognizable colors
 */
export const CONTAINER_STYLES: ContainerStyles = {
  'cloud-provider': {
    aws: {
      bgColor: '#FF9900',      // AWS Orange
      textColor: '#232F3E',    // AWS Dark Blue
      borderColor: '#FF9900',
      borderStyle: 'solid',
      icon: '☁️'
    },
    azure: {
      bgColor: '#0078D4',      // Azure Blue
      textColor: '#FFFFFF',
      borderColor: '#0078D4',
      borderStyle: 'solid',
      icon: '⛅'
    },
    gcp: {
      bgColor: '#4285F4',      // GCP Blue
      textColor: '#FFFFFF',
      borderColor: '#4285F4',
      borderStyle: 'solid',
      icon: '☁️'
    },
    generic: {
      bgColor: '#6B7280',      // Gray
      textColor: '#FFFFFF',
      borderColor: '#6B7280',
      borderStyle: 'solid',
      icon: '☁️'
    }
  },
  'layer': {
    bgColor: '#10B981',        // Emerald Green
    textColor: '#FFFFFF',
    borderColor: '#10B981',
    borderStyle: 'solid',
    icon: '📦'
  },
  'network-zone': {
    bgColor: '#8B5CF6',        // Purple
    textColor: '#FFFFFF',
    borderColor: '#8B5CF6',
    borderStyle: 'dashed',     // Dashed for network boundaries
    icon: '🌐'
  },
  'service-group': {
    bgColor: '#F59E0B',        // Amber
    textColor: '#FFFFFF',
    borderColor: '#F59E0B',
    borderStyle: 'solid',
    icon: '⚙️'
  }
}

/**
 * Get style configuration for a container
 */
export function getContainerStyle(
  type: keyof ContainerStyles,
  provider?: 'aws' | 'azure' | 'gcp' | 'generic'
): ContainerStyleConfig {
  if (type === 'cloud-provider' && provider) {
    return CONTAINER_STYLES['cloud-provider'][provider]
  }

  if (type === 'cloud-provider') {
    return CONTAINER_STYLES['cloud-provider'].generic
  }

  return CONTAINER_STYLES[type] as ContainerStyleConfig
}

/**
 * Opacity constants for professional look
 */
export const CONTAINER_OPACITY = {
  HEADER: 1.0,      // Solid header for readability
  BODY: 0.1,        // Very transparent body to see nodes through
  BORDER: 1.0,      // Solid border for clear boundaries
  HOVER: 0.15,      // Slightly more opaque on hover
  DROP_TARGET: 0.2  // Even more visible when drop target
}

/**
 * Layout constants
 */
export const CONTAINER_LAYOUT = {
  HEADER_HEIGHT: 40,
  HEADER_PADDING: '12px 16px',
  BODY_PADDING: 16,
  BORDER_WIDTH: 2,
  BORDER_RADIUS: 8,
  MIN_WIDTH: 300,
  MIN_HEIGHT: 200,
  ICON_SIZE: 20,
  FONT_SIZE: 14,
  FONT_WEIGHT: 'bold' as const,
  AUTO_RESIZE_PADDING: 32
}
